// **HostLeaderboard** — the projected leaderboard screen (`client-home`'s "the host leaderboard
// names which kind of score it is showing" requirement; sketch
// `prototype/sketches/tournament-big-screen/host-leaderboard.html`).
//
// A composable component: takes its transport as an input, constructs no HTTP client of its
// own, polls (never streams), and never asks the simulation for progress. The winner's prompts
// render as **plain text** — player-authored text on a projected screen must not be rendered as
// markup of any kind, so this component never interpolates them into HTML.

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { HostLeaderboardDto, HostTournamentTransport } from '../../net/rounds-host-http';
import { Button } from '../components/core/Button';

export interface HostLeaderboardProps {
  transport: HostTournamentTransport;
  /** Moves on to the battle-royale playback. Never called by this screen on its own — only the
   * presenter's press does. */
  onWatch?: () => void;
  /** Whether the winner's prompts are on the projector yet. Defaults to `true` — the board shows
   * them as soon as the read carries a winner, which is what a self-driving host screen wants. A
   * *scripted* host (the demo shell) reveals them on their own beat and passes `false` until then;
   * a board with no winner shows nothing either way. */
  showWinnerPrompts?: boolean;
  pollIntervalMs?: number;
}

const DEFAULT_POLL_MS = 2000;

export function HostLeaderboard({
  transport,
  onWatch,
  showWinnerPrompts = true,
  pollIntervalMs = DEFAULT_POLL_MS,
}: HostLeaderboardProps) {
  const [board, setBoard] = useState<HostLeaderboardDto | null>(null);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const poll = (): void => {
      void transport.readLeaderboard().then(
        (dto) => {
          if (!cancelled && liveRef.current) {
            setBoard(dto);
          }
        },
        () => undefined,
      );
    };
    poll();
    const id = setInterval(poll, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [transport, pollIntervalMs]);

  const rows = board?.rows ?? [];
  const graded = board !== null && board.winner !== null;
  /**
   * **The grading gap, named** (E2E round 2, N4).
   *
   * A 150-colony round takes minutes to simulate, and for all of it every row is legitimately
   * `absent` — a defaulted submission names no candidate — so the board told a room that nobody
   * had run while the battle royale it was about to show them was running. The round's own
   * recorded status is the fact that separates the two, and it is all this reads: no progress
   * fraction and no ETA, because the read deliberately never asks the simulation for either.
   */
  const simulating = !graded && board?.roundStatus === 'simulating';
  const winner = showWinnerPrompts ? (board?.winner ?? null) : null;

  return (
    <div data-testid="host-leaderboard" style={S.screen}>
      {/* Named, never an inferred caption. */}
      <div data-testid="host-leaderboard-kind" role="status" style={S.kind}>
        {graded
          ? 'Final placements'
          : simulating
            ? 'Simulating the battle royale…'
            : 'Practice runs — not yet a result'}
      </div>

      {winner !== null && (
        // **The winner is announced, not mentioned.** The board's own headline, above the table,
        // at the size the back row reads — the beat the script calls for ("congratulations to X")
        // had no visual moment at all before this.
        <div data-testid="host-leaderboard-winner-callout" style={S.callout}>
          {winner.displayName}
        </div>
      )}

      <ol data-testid="host-leaderboard-rows" style={S.rows}>
        {rows.map((row) => (
          <li key={row.joinOrdinal} data-testid={`host-leaderboard-row-${row.joinOrdinal}`} style={S.row}>
            <span data-testid={`host-leaderboard-place-${row.joinOrdinal}`} style={S.place}>
              {row.score.kind === 'graded' ? `#${row.score.placement}` : '—'}
            </span>
            <span style={S.who}>{row.displayName}</span>
            {row.score.kind === 'absent' ? (
              // `absent` means "no candidate stands behind this member's submission" — which is
              // true both of somebody who never ran and of somebody who ran and never selected.
              // "no run yet" claimed the first of both; "no run selected yet" is what the row
              // actually knows, and while the round simulates the row's subject is the result.
              <span data-testid={`host-leaderboard-score-${row.joinOrdinal}`} style={S.absent}>
                {simulating ? '— awaiting the result' : '— no run selected yet'}
              </span>
            ) : (
              // The placement moved into its own column, so the score column is the number alone
              // in both readings — one thing to read per column, which is what makes a table
              // legible at distance.
              <span data-testid={`host-leaderboard-score-${row.joinOrdinal}`} style={S.score}>
                {row.score.score}
                <small style={S.unit}>bees</small>
              </span>
            )}
          </li>
        ))}
      </ol>

      {/* **No empty frame** (E2E round-2 finding N-6, 2026-08-09). A bot or a passive player can
          win having never sent the Beekeeper a message, so `prompts` arrives as `[]` — genuinely
          nothing to show, not a read that failed. Rendering the card anyway drew a label ("Ada's
          prompts") over an empty list: a framed, titled box with nothing in it. The winner is
          still announced above (the callout is a separate fact, unaffected); this card says
          nothing when it has nothing to say, rather than saying it emptily. */}
      {winner !== null && winner.prompts.length > 0 && (
        <div data-testid="host-leaderboard-winner" style={S.card}>
          <div style={S.cardLabel}>{winner.displayName}’s prompts</div>
          {/* Plain text, never markup — the prompts are player-authored and untrusted. Each is
              rendered inside its own text node, which React always escapes; there is no
              `dangerouslySetInnerHTML` anywhere in this file for it to reach. */}
          <ul style={S.prompts}>
            {winner.prompts.map((prompt, index) => (
              <li key={index} data-testid={`host-leaderboard-winner-prompt-${index}`} style={S.prompt}>
                {prompt}
                {winner.promptsTruncated.includes(index) && (
                  <span data-testid={`host-leaderboard-winner-prompt-truncated-${index}`}> (truncated)</span>
                )}
              </li>
            ))}
          </ul>
          {winner.promptsOmitted > 0 && (
            <p data-testid="host-leaderboard-winner-omitted" style={S.omitted}>
              {winner.promptsOmitted} earlier prompt{winner.promptsOmitted === 1 ? '' : 's'} omitted.
            </p>
          )}
        </div>
      )}

      {onWatch !== undefined && (
        <Button size="lg" data-testid="host-leaderboard-watch" onClick={onWatch}>
          Watch the battle royale
        </Button>
      )}
    </div>
  );
}

/**
 * **Projector framing** (E2E round 3, polish; sketch
 * `prototype/sketches/tournament-big-screen/host-leaderboard.html`).
 *
 * This beat is 1920×1080 on a wall, read from the back of a room, and it shipped as default-size
 * text in the top-left corner of an empty screen — illegible next to the QR and spectate surfaces
 * either side of it in the script. The sizes below are the sketch's own big-screen scale, over the
 * kit's tokens; nothing here is a new colour or a new font.
 *
 * Inline rather than a stylesheet for the same reason every other host surface is: these components
 * are composed into two different roots (the rounds host flow and the demo shell) and carry their
 * own presentation rather than depending on a class the root remembered to load.
 */
const S = {
  screen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 22,
    padding: '48px 64px',
    minHeight: '100%',
    background: 'var(--surface-app)',
    color: 'var(--ink-1)',
    fontFamily: 'var(--font-body)',
  },
  kind: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: '34px',
    color: 'var(--ink-2)',
    textAlign: 'center',
  },
  callout: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: '72px',
    lineHeight: 1.05,
    color: 'var(--accent-strong)',
    textAlign: 'center',
  },
  rows: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    width: '100%',
    maxWidth: 900,
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 20,
    background: 'var(--surface-card)',
    border: '1px solid var(--line)',
    borderRadius: 'var(--r-md)',
    padding: '12px 22px',
  },
  place: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: '32px',
    color: 'var(--ink-3)',
    fontVariantNumeric: 'tabular-nums',
    minWidth: 72,
  },
  who: { flex: 1, fontSize: '30px', color: 'var(--ink-1)' },
  score: {
    fontFamily: 'var(--font-display)',
    fontWeight: 600,
    fontSize: '36px',
    color: 'var(--ink-1)',
    fontVariantNumeric: 'tabular-nums',
  },
  unit: { fontSize: '18px', color: 'var(--ink-3)', marginLeft: 6 },
  absent: { fontSize: '24px', color: 'var(--ink-3)' },
  card: {
    width: '100%',
    maxWidth: 900,
    background: 'var(--surface-card)',
    border: '1px solid var(--hud-line)',
    borderRadius: 'var(--r-lg)',
    boxShadow: 'var(--shadow-hud)',
    padding: 24,
  },
  cardLabel: {
    fontWeight: 800,
    fontSize: '16px',
    textTransform: 'uppercase',
    letterSpacing: 'var(--tracking-caps)',
    color: 'var(--ink-3)',
    marginBottom: 12,
  },
  prompts: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 },
  prompt: {
    background: 'var(--paper-1)',
    borderRadius: 'var(--r-md)',
    padding: '10px 14px',
    fontSize: '22px',
    lineHeight: 1.45,
  },
  omitted: { margin: '12px 0 0', fontSize: '18px', color: 'var(--ink-3)' },
} satisfies Record<string, CSSProperties>;
